/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { FC, SetStateAction, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box } from '@material-ui/core';
import { BaselineStageProps, BeefFarms } from '../../common';
import { AnimalType, StageType } from '../../../../../../graphql/types';
import { defaultValues } from '../../baselineDefaultValues';
import { stageDataPartBeef } from '../../../../../modules/Farms/Baseline/validationSchema/beefBaselineValidation';
import { Origin } from '../types';
import { StageButtonBox } from '../../StageButtonBox_v2';
import BeefEmissionsDialog from './BeefEmissionsDialog';
import BeefOutputGrowingDialog from './BeefOutputGrowingDialog';

import BeefInputBreedingDialog from './BeefInputBreedingDialog';
import BeefHousingAndManureDialog from './BeefHousingAndManureDialog';
import BeefOutputBreedingDialog from './BeefOutputBreedingDialog';
import BeefFeedGrowingDialog from './BeefFeedGrowingDialog';
import BeefInputGrowingDialog from './BeefInputGrowingDialog';
import { BeefBaseline } from '../../../../models/Baseline/BeefBaseline';
import BeefFeedBreedingDialog from './BeefFeedBreedingDialog';

interface BeefStagesProps extends Omit<BaselineStageProps, 'baseline'> {
  farms: BeefFarms[];
  farmId: string;
  farmName: string;
  productionProcessName: string;
  baseline: BeefBaseline;
  origins: Origin[];
  animalType: AnimalType.Beef;
  calculateCompleteness: (validationFunction: any, prefix: string) => number;
}

interface CloseDialogData {
  type: string;
  setFunc: React.Dispatch<React.SetStateAction<number>>;
  prefix: 'input' | 'output' | 'feed' | 'housing' | 'emissions';
}

interface InitialCalcuationData {
  prefix: 'input' | 'output' | 'feed' | 'housing' | 'emissions';
}

const BeefStages: FC<BeefStagesProps> = ({
  farms,
  farmName,
  farmId,
  productionProcessName,
  stageIndex,
  baseline,
  compoundFeeds,
  singleIngredients,
  origins,
  item,
  formType,
  stageType = StageType.Growing,
  animalType,
  calculateCompleteness,
}) => {
  const formContext = useFormContext();
  const [activeDialog, setActiveDialog] = useState<string>('');

  const showDialog = (dialog: SetStateAction<string>) => {
    setActiveDialog(dialog);
  };

  const stagePrefix = `stages.${stageIndex}`;
  const [inputBreedingCompleteness, setInputBreedingCompleteness] = useState(0);
  const [inputGrowingCompleteness, setInputGrowingCompleteness] = useState(0);
  const [feedBreedingCompleteness, setFeedBreedingCompleteness] = useState(0);
  const [feedGrowingCompleteness, setFeedGrowingCompleteness] = useState(0);
  const [housingAndManureCompletness, setHousingAndManureCompletness] =
    useState(0);
  const [outputBreedingCompleteness, setOutputBreedingCompleteness] =
    useState(0);
  const [outputGrowingCompleteness, setOutputGrowingCompleteness] = useState(0);
  const [emissionCompleteness, setEmissionCompleteness] = useState(0);

  const feedCompletenessPercentage = () => {
    switch (stageType) {
      case StageType.Breeding:
        return feedBreedingCompleteness;
      case StageType.Growing:
        return feedGrowingCompleteness;
      default:
        return 0;
    }
  };

  const inputCompletenessPercentage = () => {
    switch (stageType) {
      case StageType.Breeding:
        return inputBreedingCompleteness;
      case StageType.Growing:
        return inputGrowingCompleteness;
      default:
        return 0;
    }
  };

  const outputCompletenessPercentage = () => {
    switch (stageType) {
      case StageType.Breeding:
        return outputBreedingCompleteness;
      case StageType.Growing:
        return outputGrowingCompleteness;
      default:
        return 0;
    }
  };

  const initialCalculation = (
    initialCalcuationData: InitialCalcuationData
  ) => {
    const validationObject = item.stageData[initialCalcuationData.prefix];
    const defaultObject =
      defaultValues[animalType]?.stages[0]?.stageData[
        initialCalcuationData.prefix
      ];
    if (item.newStage && isEqual(validationObject, defaultObject))
      return 0;
    return calculateCompleteness(
      stageDataPartBeef,
      `stages[${stageIndex}].stageData.${initialCalcuationData.prefix}`
    );
  };

  const closeDialog = (closeDialogData: CloseDialogData) => {
    const completeness = calculateCompleteness(
      stageDataPartBeef,
      `stages[${stageIndex}].stageData.${closeDialogData.prefix}`
    );
    if (formType !== 'view' && closeDialogData.type === 'confirm') {
      closeDialogData.setFunc(completeness);
    } else if (closeDialogData.type === 'reset') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      if (!completeness) formContext.trigger(`${stagePrefix}.stageData.${activeDialog}`);
      else formContext.clearErrors(`${stagePrefix}.stageData.${activeDialog}`);
    }
    setActiveDialog('');
  };

  const getSelectableStages = (): string[] => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const currentBaselineValues = formContext.getValues()?.stages
      ? formContext.getValues()?.stages.map((stage: { id: string }) => stage.id)
      : [];
    return [
      ...farms.map((farm) => farm.stageId),
      ...currentBaselineValues,
    ] as string[];
  };

  const checkInternalSources = () => {
    const allStageIds = getSelectableStages();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    formContext.getValues(`${stagePrefix}.stageData.input.internalSources`)?.forEach(
      (internalSource: { originStageId: string }, index: number) => {
        if (
          !allStageIds.includes(internalSource.originStageId) &&
          internalSource.originStageId
        ) {
          formContext.setValue(
            `${stagePrefix}.stageData.input.internalSources[${index}].farmId`,
            ''
          );
          formContext.setValue(
            `${stagePrefix}.stageData.input.internalSources[${index}].originStageId`,
            ''
          );
          formContext.trigger(
            `${stagePrefix}.stageData.input.internalSources[${index}]`
          );
        }
      }
    );
  };

  useEffect(() => {
    if (formType === 'edit' && !item.newStage) {
      if (stageType === StageType.Growing) {
        setInputGrowingCompleteness(
          initialCalculation({ prefix: 'input' })
        );
        setFeedGrowingCompleteness(
          initialCalculation({ prefix: 'feed' })
        );
        setHousingAndManureCompletness(
          initialCalculation({ prefix: 'housing' })
        );
        setOutputGrowingCompleteness(
          initialCalculation({ prefix: 'output' })
        );
        setEmissionCompleteness(
          initialCalculation({ prefix: 'emissions' })
        );
      }
      if (stageType === StageType.Breeding) {
        setInputBreedingCompleteness(
          initialCalculation({ prefix: 'input' })
        );
        setFeedBreedingCompleteness(
          initialCalculation({ prefix: 'feed' })
        );
        setHousingAndManureCompletness(
          initialCalculation({ prefix: 'housing' })
        );
        setOutputBreedingCompleteness(
          initialCalculation({ prefix: 'output' })
        );
        setEmissionCompleteness(
          initialCalculation({ prefix: 'emissions' })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  // Check if selected stages are existing or not
  // In case of some baseline deleted, the loaded data needs
  // to be removed so the user needs to select a different stage
  useEffect(() => {
    if (farms?.length) {
        checkInternalSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageType, farms, formType]);

  return (
    <>
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${stagePrefix}.id`}
        defaultValue={item.id}
      />
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${stagePrefix}.name`}
        defaultValue={item.name}
      />
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${stagePrefix}.type`}
        defaultValue={item.type}
      />
      {stageType === StageType.Breeding && activeDialog === 'input' && (
        <BeefInputBreedingDialog
          farms={farms}
          farmId={farmId}
          farmName={farmName}
          productionProcessName={productionProcessName}
          formVisible={activeDialog === 'input'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setInputBreedingCompleteness,
              prefix: 'input',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
        />
      )}
      {stageType === StageType.Growing && activeDialog === 'input' && (
        <BeefInputGrowingDialog
          farms={farms}
          farmId={farmId}
          farmName={farmName}
          productionProcessName={productionProcessName}
          formVisible={activeDialog === 'input'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setInputGrowingCompleteness,
              prefix: 'input',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
        />
      )}
      {stageType === StageType.Breeding && activeDialog === 'feed' && (
        < BeefFeedBreedingDialog
          formVisible={activeDialog === 'feed'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setFeedBreedingCompleteness,
              prefix: 'feed',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
          compoundFeeds={compoundFeeds}
          singleIngredients={singleIngredients}
          origins={origins}
          formErrors={
            formContext.errors?.stages &&
            formContext.errors?.stages[stageIndex]?.stageData?.feed
          }
        />
      )}
      {stageType === StageType.Growing && activeDialog === 'feed' && (
        <BeefFeedGrowingDialog
          formVisible={activeDialog === 'feed'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setFeedGrowingCompleteness,
              prefix: 'feed',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
          compoundFeeds={compoundFeeds}
          singleIngredients={singleIngredients}
          origins={origins}
        />
      )}
      {activeDialog === 'housing' && (
        <BeefHousingAndManureDialog
          formVisible={activeDialog === 'housing'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setHousingAndManureCompletness,
              prefix: 'housing',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
          stageType={stageType}
          formErrors={
            formContext.errors?.stages &&
            formContext.errors?.stages[stageIndex]?.stageData?.housing
          }
        />
      )}
      {stageType === StageType.Breeding && activeDialog === 'output' && (
        <BeefOutputBreedingDialog
          formVisible={activeDialog === 'output'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setOutputBreedingCompleteness,
              prefix: 'output',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
        />
      )}
      {stageType === StageType.Growing && activeDialog === 'output' && (
        <BeefOutputGrowingDialog
          formVisible={activeDialog === 'output'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setOutputGrowingCompleteness,
              prefix: 'output',
            })
          }
          baseline={baseline}
          itemIndex={stageIndex}
        />
      )}
      {activeDialog === 'emissions' && (
        <BeefEmissionsDialog
          formVisible={activeDialog === 'emissions'}
          formType={formType}
          handleCancel={(type: string) =>
            closeDialog({
              type,
              setFunc: setEmissionCompleteness,
              prefix: 'emissions',
            })
          }
          itemIndex={stageIndex}
          stageType={stageType as StageType}
        />
      )}
      <Box
        style={{ display: 'flex', width: '100%' }}
        flexDirection="row"
        justifyContent="space-between"
      >
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE"
          iconCode="general/check-heart"
          descriptionTitle="SUSTELL.STAGE.ANIMAL_DETAILS"
          description="SUSTELL.STAGE.INPUT_DATA"
          completness={inputCompletenessPercentage()}
          error={
            formContext.errors?.stages && formContext.errors?.stages[stageIndex]?.stageData?.input
          }
          handleOpen={() => showDialog('input')}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.FEED.TITLE"
          iconCode="shapes/cube-02"
          descriptionTitle="SUSTELL.STAGE.FEED"
          description="SUSTELL.STAGE.INPUT_DATA"
          completness={feedCompletenessPercentage()}
          error={
            formContext.errors?.stages && formContext.errors?.stages[stageIndex]?.stageData?.feed
          }
          handleOpen={() => showDialog('feed')}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE"
          iconCode="general/building-06"
          completness={housingAndManureCompletness}
          descriptionTitle="SUSTELL.STAGE.HOUSING"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            formContext.errors?.stages &&
            formContext.errors?.stages[stageIndex]?.stageData?.housing
          }
          handleOpen={() => showDialog('housing')}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE"
          iconCode="arrows/arrow-circle-broken-right"
          descriptionTitle="SUSTELL.STAGE.OUTPUT"
          description="SUSTELL.STAGE.INPUT_DATA"
          completness={outputCompletenessPercentage()}
          error={
            formContext.errors?.stages &&
            formContext.errors?.stages[stageIndex]?.stageData?.output
          }
          handleOpen={() => showDialog('output')}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.EMISSIONS.TITLE"
          iconCode="maps-travel/globe-04"
          descriptionTitle="SUSTELL.STAGE.MITTIGATION"
          description="SUSTELL.STAGE.INPUT_DATA"
          completness={emissionCompleteness}
          handleOpen={() => showDialog('emissions')}
          error={
            formContext.errors?.stages &&
            formContext.errors?.stages[stageIndex]?.stageData?.emissions
          }
        />
      </Box>
    </>
  );
};

export default BeefStages;
